import { Modal } from 'react-bootstrap';
import { Button } from '@Components/Button';
import { Appearance, Design } from '@Components/designConstants';
import { GroupedMappingRow, MappingTabConfig } from './client-data-mapping-constants';
import { useClientTargetPicker } from './ClientTargetPicker/ClientTargetPicker';

interface AddClientsModalProps {
  tab: MappingTabConfig;
  // The row being extended -- an already-assigned grouped row. Its existing cids are excluded
  // from the picker so a user can't accidentally pick a client the item is already assigned to.
  row: GroupedMappingRow;
  onHide: () => void;
  // Fire-and-forget: the modal collects target cids and closes; the parent wraps the batched
  // assign calls in optimisticMutate. See ClientDataMapping.handleAddClientsSubmit.
  onSubmit: (targetCids: string[]) => void;
}

// Attach additional clients to a row that's already in the Assigned segment. Distinct from
// CompanyPickerModal:
//   - No fromCid / reassign semantics -- purely additive; existing assignments are preserved.
//   - Mark-as-generic is intentionally suppressed here -- going generic means dropping *all*
//     existing client assignments, which is not what "add more" implies. The user takes the
//     Mark-as-generic action from the same row's globe icon instead.
export const AddClientsModal = ({ tab, row, onHide, onSubmit }: AddClientsModalProps) => {
  const target = useClientTargetPicker({
    tab,
    excludeCids: row.cids,
    checkboxId: 'add-clients-mark-generic',
    hideMarkGeneric: true
  });

  const handleSave = () => {
    if (!target.isValid) return;
    onSubmit(target.targetCids);
    onHide();
  };

  return (
    <Modal show onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Add clients to &ldquo;{row.displayName}&rdquo;</Modal.Title>
      </Modal.Header>
      <Modal.Body>{target.render()}</Modal.Body>
      <Modal.Footer>
        <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL} onClick={onHide}>
          Cancel
        </Button>
        <Button disabled={!target.isValid} onClick={handleSave}>
          Add
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
